import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import {
  IFinanceRoundDataModel,
  IInvestmentRoundSummaryDataModel,
} from '../../../data-models/finance-round.data-model';
import { IRoundReconDataModel } from '../../../data-models/finance-round-recon.data-model';
import { IRoundDataModel } from '../../../data-models/round.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import {
  fetchAllInvestmentRounds,
  fetchInvestmentRoundSummaries,
  fetchRoundRecon,
  fetchTransactionsByCompany2,
} from '../../../services/queries/MaggieTransactionQueries';
import { readThroughAtom } from '../../../services/boot-cache/bootCache';
import { roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { defaultFormatter, FormatterService } from '../../../util/formatter-service';
import { getForesightStore } from '../../../util/jotai-store';
import { InvestmentRoundFormatter } from '../../../util/formatters/InvestmentRoundFormatter';
import { StageFormatter } from '../../../util/formatters/StageFormatter';

type InvestmentRoundsValue = IFinanceRoundDataModel[] | Promise<IFinanceRoundDataModel[]>;
type InvestmentRoundSummariesValue =
  IInvestmentRoundSummaryDataModel[] | Promise<IInvestmentRoundSummaryDataModel[]>;
type TransactionsValue = ITransactionDataModel[] | Promise<ITransactionDataModel[]>;
type RoundReconValue = IRoundReconDataModel[] | Promise<IRoundReconDataModel[]>;

/**
 * Bumping it, then resetting {@link allInvestmentRoundsStateJ} and {@link investmentRoundSummariesStateJ},
 * refetches the rounds on their next read.
 */
export const investmentRoundsReloadJ = atom(0);

/**
 * Default fetches every investment round (across all companies) on first
 * subscribe. Refetched by {@link useInvalidateFinanceData.invalidateInvestments}.
 */
export const allInvestmentRoundsStateJ = atomWithDefault<InvestmentRoundsValue>(async (get) => {
  get(investmentRoundsReloadJ);
  return fetchAllInvestmentRounds();
});

export const allInvestmentRoundsStateLoadableJ = loadable(allInvestmentRoundsStateJ);

export const allInvestmentRoundsMapStateJ = atom(
  async (get): Promise<Map<number, IFinanceRoundDataModel>> => {
    const rounds = await get(allInvestmentRoundsStateJ);
    const map = new Map<number, IFinanceRoundDataModel>();
    rounds.forEach((r) => map.set(r.id, r));
    return map;
  }
);

/**
 * Every round as id, name, company and stage: what the investmentRound and stage formatters need, read
 * at boot from GET /round/summary instead of the full GET /round. Kept apart from
 * {@link allInvestmentRoundsStateJ}, so the two reads never share a cache entry.
 */
export const investmentRoundSummariesStateJ = atomWithDefault<InvestmentRoundSummariesValue>(
  (get): Promise<IInvestmentRoundSummaryDataModel[]> => {
    get(investmentRoundsReloadJ);
    return readThroughAtom(investmentRoundSummariesStateJ, 'roundSummaries', fetchInvestmentRoundSummaries);
  }
);

export const investmentRoundSummariesMapStateJ = atom(
  async (get): Promise<Map<number, IInvestmentRoundSummaryDataModel>> => {
    const summaries = await get(investmentRoundSummariesStateJ);
    return new Map(summaries.map((summary) => [summary.id, summary]));
  }
);

/**
 * The round summaries, resolved only once the investmentRound and stage formatters know those rounds.
 * Grids that format with either id read this, so they wait for GET /round/summary behind their own
 * loading state rather than the app boot waiting for it.
 */
export const investmentRoundFormattersStateJ = atom(
  async (get): Promise<Map<number, IInvestmentRoundSummaryDataModel>> => {
    const investmentRounds = await get(investmentRoundSummariesMapStateJ);
    registerInvestmentRoundFormatters(investmentRounds, get(roundsByIdMapAtom));
    return investmentRounds;
  }
);

export function registerInvestmentRoundFormatters(
  investmentRounds: Map<number, IInvestmentRoundSummaryDataModel>,
  roundsById: Map<number, IRoundDataModel>
) {
  const service = FormatterService.get();
  service.setFormatterForId('investmentRound', new InvestmentRoundFormatter().init(investmentRounds).format);
  service.setFormatterForId(
    'stage',
    new StageFormatter().init({ investmentMap: investmentRounds, stageMap: roundsById }).format
  );
}

/**
 * Registers the investmentRound and stage formatters from the app's own rounds again. The formatter
 * service is global, so a frosted sample, reading this atom in its own store, registers its sample rounds
 * over them while it is shown; this puts the real ones back once it is gone.
 */
export function restoreInvestmentRoundFormatters(store = getForesightStore()) {
  // Nothing to put back without a formatter service (before the boot sets it up, or after sign-out).
  if (!FormatterService.isInitialized()) {
    return;
  }
  Promise.resolve(store.get(investmentRoundSummariesMapStateJ))
    .then((rounds) => registerInvestmentRoundFormatters(rounds, store.get(roundsByIdMapAtom)))
    .catch(() => {
      // As at boot: the ids rather than nothing.
      const service = FormatterService.get();
      service.setFormatterForId('investmentRound', defaultFormatter);
      service.setFormatterForId('stage', defaultFormatter);
    });
}

export const roundsByCompanyIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<IFinanceRoundDataModel[]> => {
    const all = await get(allInvestmentRoundsStateJ);
    return all.filter((r) => r.companyId === companyId);
  })
);

export const roundsByCompanyIdStateLoadableJ = atomFamily((companyId: number) =>
  loadable(roundsByCompanyIdStateJ(companyId))
);

export const companyRoundsByIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<number, IFinanceRoundDataModel>> => {
    const rounds = await get(roundsByCompanyIdStateJ(companyId));
    const map = new Map<number, IFinanceRoundDataModel>();
    rounds.forEach((r) => map.set(r.id, r));
    return map;
  })
);

/**
 * Sentinel-guarded: returns `[]` for non-positive ids so consumers using
 * the `?? -1` pattern (Transactions3, RoundTable, InvestmentSelectOrCreateForm)
 * don't fire a spurious `fetchTransactionsByCompany2(-1)` request.
 */
export const financeTransactionsByCompanyStateJ = atomFamily((companyId: number) =>
  atomWithDefault<TransactionsValue>(async () => {
    if (companyId <= 0) {
      return [];
    }
    return fetchTransactionsByCompany2(companyId);
  })
);

export const financeTransactionsByCompanyStateLoadableJ = atomFamily((companyId: number) =>
  loadable(financeTransactionsByCompanyStateJ(companyId))
);

export const transactionsByIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<string, ITransactionDataModel>> => {
    const transactions = await get(financeTransactionsByCompanyStateJ(companyId));
    const map = new Map<string, ITransactionDataModel>();
    // A merged row's ledger rows are actionable on their own (History, Manage Round), so they are indexed too.
    transactions.flatMap((t) => [t, ...(t.ledgerRows ?? [])]).forEach((t) => map.set(String(t.id), t));
    return map;
  })
);

export const roundReconStateJ = atomWithDefault<RoundReconValue>(async () => fetchRoundRecon());

export const roundReconStateLoadableJ = loadable(roundReconStateJ);

/**
 * Increment to invalidate the all-transactions-by-date query (read by
 * `AllTransactions.tsx`; written by `useInvalidateFinanceData.invalidateTransactions`).
 */
export const forceAllTransactionsByDateUpdateJ = atom<number>(0);
